"use client";

import type { User } from "@/types";
import type { Dispatch, SetStateAction } from "react";
import { createContext, useContext } from "react";

/**
 * List of available layout types.
 */
export const layoutList = [
  "Vertical",
  "Hovered",
  "Horizontal",
  "Two Column"
] as const;
export type LayoutType = (typeof layoutList)[number];

/**
 * Interface for the Layout Context.
 */
export interface LayoutContextType {
  /** The fully authenticated user object or undefined if not logged in. */
  userAuth: Partial<User> | null;
  /** Current layout mode. */
  layout: LayoutType;
  /** Function to change the layout mode. */
  changeLayout: (newLayout: LayoutType) => void;
  /** Current text direction (ltr or rtl). */
  dir: string;
  /** Function to change the text direction. */
  changeDir: (newDir: string) => void;
  /** Translation function. */
  t: (key: string) => string;

  /** Modal visibility state. */
  showModal: boolean;
  /** Function to toggle modal visibility. */
  setShowModal: Dispatch<SetStateAction<boolean>>;

  /** Sidebar visibility state. */
  sidebarIsOpen: boolean;
  /** Function to toggle sidebar visibility. */
  setSidebarIsOpen: Dispatch<SetStateAction<boolean>>;
}

export const LayoutContext = createContext<LayoutContextType | undefined>(
  undefined
);

/**
 * Custom hook to consume the LayoutContext.
 *
 * @returns {LayoutContextType} The context value.
 * @throws {Error} If used outside of a LayoutProvider.
 */
export const useLayout = (): LayoutContextType => {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error("useLayout must be used within a LayoutProvider");
  }
  return context;
};
